import { create } from 'zustand'
import { usersApi } from '../api/users'
import type {
  User,
  UserFilters,
  CreateUserData,
  UpdateUserData,
} from '../types'

interface UserState {
  users: User[]
  currentUser: User | null
  isLoading: boolean
  error: string | null
  /** HTTP-код ошибки при загрузке currentUser (403, 404, 500 и т.д.) */
  errorStatus: number | null
  pagination: {
    currentPage: number
    lastPage: number
    total: number
  }
  filters: UserFilters
  /** Increment to force UsersPage refetch (e.g. after contract status change on details). */
  usersListVersion: number

  // Actions
  fetchUsers: (filters?: UserFilters) => Promise<void>
  fetchUser: (id: number) => Promise<void>
  createUser: (data: CreateUserData) => Promise<User>
  updateUser: (id: number, data: UpdateUserData) => Promise<void>
  deleteUser: (id: number) => Promise<void>
  cancelContract: (userId: number) => Promise<void>
  invalidateUsersList: () => void
  setFilters: (filters: UserFilters) => void
  setCurrentUser: (user: User | null) => void
  clearError: () => void
}

export const useUserStore = create<UserState>((set, get) => ({
  users: [],
  currentUser: null,
  isLoading: false,
  error: null,
  errorStatus: null,
  pagination: {
    currentPage: 1,
    lastPage: 1,
    total: 0,
  },
  filters: {},
  usersListVersion: 0,

  fetchUsers: async (filters) => {
    set({ isLoading: true, error: null })
    try {
      const mergedFilters = { ...get().filters, ...filters }
      const response = await usersApi.getAll(mergedFilters)
      set({
        users: response.data,
        pagination: {
          currentPage: response.current_page,
          lastPage: response.last_page,
          total: response.total,
        },
        filters: mergedFilters,
      })
    } catch (error: unknown) {
      const err = error as { message?: string }
      set({ error: err.message || 'Ошибка загрузки пользователей' })
    } finally {
      set({ isLoading: false })
    }
  },

  fetchUser: async (id) => {
    set({ isLoading: true, error: null, errorStatus: null })
    try {
      const user = await usersApi.getById(id)
      set({ currentUser: user })
    } catch (error: unknown) {
      const err = error as { message?: string; status?: number }
      set({
        currentUser: null,
        error: err.message || 'Ошибка загрузки пользователя',
        errorStatus: err.status ?? null,
      })
    } finally {
      set({ isLoading: false })
    }
  },

  createUser: async (data) => {
    set({ isLoading: true, error: null })
    try {
      const newUser = await usersApi.create(data)
      set((state) => ({ users: [newUser, ...state.users] }))
      return newUser
    } catch (error: unknown) {
      const err = error as { message?: string }
      set({ error: err.message || 'Ошибка создания пользователя' })
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  updateUser: async (id, data) => {
    set({ isLoading: true, error: null })
    try {
      const updatedUser = await usersApi.update(id, data)
      set((state) => ({
        users: state.users.map((u) => (u.id === id ? updatedUser : u)),
        currentUser: state.currentUser?.id === id ? updatedUser : state.currentUser,
        usersListVersion:
          data.contract_status !== undefined ? state.usersListVersion + 1 : state.usersListVersion,
      }))
    } catch (error: unknown) {
      const err = error as { message?: string }
      set({ error: err.message || 'Ошибка обновления пользователя' })
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  deleteUser: async (id) => {
    set({ isLoading: true, error: null })
    try {
      await usersApi.delete(id)
      set((state) => ({
        users: state.users.filter((u) => u.id !== id),
        currentUser: state.currentUser?.id === id ? null : state.currentUser,
      }))
    } catch (error: unknown) {
      const err = error as { message?: string }
      set({ error: err.message || 'Ошибка удаления пользователя' })
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  cancelContract: async (userId) => {
    set({ isLoading: true, error: null })
    try {
      const updatedUser = await usersApi.cancelContract(userId)
      set((state) => ({
        users: state.users.map((u) => (u.id === userId ? updatedUser : u)),
        currentUser: state.currentUser?.id === userId ? updatedUser : state.currentUser,
      }))
    } catch (error: unknown) {
      const err = error as { message?: string }
      set({ error: err.message || 'Ошибка расторжения договора' })
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  setFilters: (filters) => set({ filters }),
  setCurrentUser: (user) => set({ currentUser: user }),
  invalidateUsersList: () => set((state) => ({ usersListVersion: state.usersListVersion + 1 })),
  clearError: () => set({ error: null, errorStatus: null }),
}))
